import { useEffect, useMemo, useState } from 'react';
import type { DesignElement } from '@canvas/shared-types';
import {
  ELEMENT_CATALOG,
  ELEMENT_CATEGORIES,
} from '@/data/elementCatalog';
import { TEMPLATE_CATALOG } from '@/data/templateCatalog';
import { PHOTO_CATALOG } from '@/data/photoCatalog';
import { ICON_CATALOG } from '@/data/iconCatalog';
import { CHART_CATALOG } from '@/data/chartCatalog';
import { DEFAULT_BRAND_KIT, brandFontAsElement } from '@/data/brandKit';
import PhotoStudioPanel from './PhotoStudioPanel';

export type DesignPanelTab =
  | 'templates'
  | 'elements'
  | 'text'
  | 'photos'
  | 'icons'
  | 'charts'
  | 'brand'
  | 'uploads'
  | 'photo-studio';

/** Nested under Elements (kept in DesignPanelTab for controlled-tab sync). */
const ELEMENT_NESTED_TABS = [
  'text',
  'photos',
  'icons',
  'charts',
  'brand',
] as const;

type ElementNestedTab = (typeof ELEMENT_NESTED_TABS)[number];

function isElementNestedTab(tab: DesignPanelTab): tab is ElementNestedTab {
  return (ELEMENT_NESTED_TABS as readonly string[]).includes(tab);
}

function isElementsLibraryTab(tab: DesignPanelTab): boolean {
  return tab === 'elements' || isElementNestedTab(tab);
}

export interface RecentUpload {
  id: string;
  name: string;
  url: string;
  source: 'storage' | 'local';
}

interface DesignPanelProps {
  onInsertElement: (element: DesignElement) => void;
  onApplyTemplate: (templateId: string) => void;
  onUploadImage?: (file: File) => void | Promise<void>;
  /** Import an MP4 / WebM / MOV as a video clip layer. */
  onUploadVideo?: (file: File) => void | Promise<void>;
  onInsertUploadUrl?: (url: string) => void;
  recentUploads?: RecentUpload[];
  uploadBusy?: boolean;
  uploadNote?: string | null;
  onApplyBrandColor?: (hex: string) => void;
  /** Controlled tab from Adobe dock (optional) */
  activeTab?: DesignPanelTab;
  onTabChange?: (tab: DesignPanelTab) => void;
  hideTabs?: boolean;
  projectId?: string;
}

function filterByAudienceAndQuery(
  items: DesignElement[],
  audience: 'all' | 'student' | 'professional',
  query: string
) {
  const q = query.trim().toLowerCase();
  return items.filter((e) => {
    if (audience !== 'all' && e.audience !== 'all' && e.audience !== audience) {
      return false;
    }
    if (!q) return true;
    return (
      e.name.toLowerCase().includes(q) || e.tags.some((tag) => tag.includes(q))
    );
  });
}

function ElementGrid({
  items,
  onInsert,
}: {
  items: DesignElement[];
  onInsert: (el: DesignElement) => void;
}) {
  return (
    <div className="design-grid elements-grid">
      {items.map((el) => (
        <button
          key={el.elementId}
          type="button"
          className="design-card element-card"
          onClick={() => onInsert(el)}
          title={el.name}
        >
          <div
            className="design-card-preview element-preview"
            style={
              el.preview.mode === 'swatch'
                ? { background: el.preview.value }
                : el.preview.mode === 'image' &&
                    el.kind !== 'icon' &&
                    el.kind !== 'diagram' &&
                    el.kind !== 'shape' &&
                    el.kind !== 'line' &&
                    el.kind !== 'frame'
                  ? {
                      backgroundImage: `url("${el.preview.value}")`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
                  : undefined
            }
          >
            {el.preview.mode === 'glyph' && (
              <span className="glyph">{el.preview.value}</span>
            )}
            {el.preview.mode === 'image' && el.kind === 'icon' && (
              <img src={el.preview.value} alt="" className="icon-preview-img" />
            )}
            {el.preview.mode === 'image' &&
              (el.kind === 'diagram' ||
                el.kind === 'shape' ||
                el.kind === 'line' ||
                el.kind === 'frame') && (
                <img
                  src={el.preview.value}
                  alt=""
                  className="element-shape-preview-img"
                />
              )}
          </div>
          <div className="design-card-meta">
            <strong>{el.name}</strong>
          </div>
        </button>
      ))}
      {items.length === 0 && (
        <p className="design-empty">No items match your filters.</p>
      )}
    </div>
  );
}

export default function DesignPanel({
  onInsertElement,
  onApplyTemplate,
  onUploadImage,
  onUploadVideo,
  onInsertUploadUrl,
  recentUploads = [],
  uploadBusy = false,
  uploadNote = null,
  onApplyBrandColor,
  activeTab,
  onTabChange,
  hideTabs = false,
  projectId = 'local',
}: DesignPanelProps) {
  const [internalTab, setInternalTab] = useState<DesignPanelTab>('elements');
  const tab = activeTab ?? internalTab;
  const setTab = (next: DesignPanelTab) => {
    onTabChange?.(next);
    if (activeTab === undefined) setInternalTab(next);
  };
  const [query, setQuery] = useState('');
  const [audience, setAudience] = useState<'all' | 'student' | 'professional'>('all');
  const [elementSection, setElementSection] = useState<string>(() =>
    activeTab && isElementNestedTab(activeTab) ? activeTab : 'shapes'
  );
  const [localBusy, setLocalBusy] = useState(false);
  const busy = uploadBusy || localBusy;

  // Nested library ids (Text/Photos/…) live under Elements — normalize parent tab
  useEffect(() => {
    if (isElementNestedTab(tab)) {
      setElementSection(tab);
      setTab('elements');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to nested tab ids
  }, [tab]);

  const elementSections = useMemo(
    () => [
      ...ELEMENT_CATEGORIES.map((c) => ({
        id: c.id,
        label: c.id === 'text' ? 'Text' : c.label,
      })),
      { id: 'photos', label: 'Photos' },
      { id: 'icons', label: 'Icons' },
      { id: 'charts', label: 'Charts' },
      { id: 'brand', label: 'Brand' },
    ],
    []
  );

  const templates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TEMPLATE_CATALOG.filter((t) => {
      if (audience !== 'all' && t.audience !== 'all' && t.audience !== audience) {
        return false;
      }
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.tags.some((tag) => tag.includes(q))
      );
    });
  }, [query, audience]);

  const elements = useMemo(() => {
    const pool = ELEMENT_CATALOG.filter((e) => e.category === elementSection);
    return filterByAudienceAndQuery(pool, audience, query);
  }, [query, audience, elementSection]);

  const photos = useMemo(
    () => filterByAudienceAndQuery(PHOTO_CATALOG, audience, query),
    [audience, query]
  );

  const icons = useMemo(
    () => filterByAudienceAndQuery(ICON_CATALOG, audience, query),
    [audience, query]
  );

  const charts = useMemo(
    () => filterByAudienceAndQuery(CHART_CATALOG, audience, query),
    [audience, query]
  );

  const tabs: { id: DesignPanelTab; label: string }[] = [
    { id: 'templates', label: 'Templates' },
    { id: 'elements', label: 'Elements' },
    { id: 'uploads', label: 'Uploads' },
    { id: 'photo-studio', label: 'Photo Studio' },
  ];

  const searchPlaceholder =
    tab === 'templates'
      ? 'Search templates…'
      : elementSection === 'photos'
        ? 'Search photos…'
        : elementSection === 'icons'
          ? 'Search icons…'
          : elementSection === 'charts'
            ? 'Search charts…'
            : elementSection === 'brand'
              ? 'Search brand…'
              : 'Search elements…';

  return (
    <aside className="design-panel">
      {!hideTabs && (
        <nav className="design-tabs" aria-label="Design library">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              className={
                item.id === 'elements'
                  ? isElementsLibraryTab(tab)
                    ? 'active'
                    : ''
                  : tab === item.id
                    ? 'active'
                    : ''
              }
              onClick={() => {
                setTab(item.id);
                setQuery('');
              }}
            >
              {item.label}
            </button>
          ))}
        </nav>
      )}

      <div className="design-panel-body">
        {tab !== 'uploads' && tab !== 'photo-studio' && (
          <>
            <input
              className="design-search"
              type="search"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />

            <div className="design-audience">
              {(['all', 'student', 'professional'] as const).map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`preset-chip ${audience === id ? 'active' : ''}`}
                  onClick={() => setAudience(id)}
                >
                  {id === 'all' ? 'All' : id === 'student' ? 'Students' : 'Pros'}
                </button>
              ))}
            </div>
          </>
        )}

        {tab === 'templates' && (
          <div className="design-grid templates-grid">
            {templates.map((tpl) => (
              <button
                key={tpl.templateId}
                type="button"
                className="design-card"
                onClick={() => {
                  if (
                    confirm(
                      `Apply “${tpl.name}”? This replaces the current canvas contents.`
                    )
                  ) {
                    onApplyTemplate(tpl.templateId);
                  }
                }}
              >
                <div
                  className="design-card-preview template-preview"
                  style={{
                    background: `linear-gradient(135deg, ${tpl.colorPalette?.[0] || '#0d7377'}, ${tpl.colorPalette?.[1] || '#eef2f6'})`,
                  }}
                >
                  <span>
                    {tpl.width}×{tpl.height}
                  </span>
                </div>
                <div className="design-card-meta">
                  <strong>{tpl.name}</strong>
                  <span>{tpl.description}</span>
                </div>
              </button>
            ))}
            {templates.length === 0 && (
              <p className="design-empty">No templates match your filters.</p>
            )}
          </div>
        )}

        {isElementsLibraryTab(tab) && (
          <>
            <div className="design-subcats">
              {elementSections.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`preset-chip ${elementSection === cat.id ? 'active' : ''}`}
                  onClick={() => {
                    setElementSection(cat.id);
                    // Keep parent tab on Elements (nested ids still work if passed)
                    if (tab !== 'elements') setTab('elements');
                    setQuery('');
                  }}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {elementSection === 'photos' && (
              <>
                <p className="field-hint" style={{ margin: '0 0 8px', fontSize: '0.72rem' }}>
                  Local stock pack (SVG). Click to place on the artboard.
                </p>
                <ElementGrid items={photos} onInsert={onInsertElement} />
              </>
            )}

            {elementSection === 'icons' && (
              <>
                <p className="field-hint" style={{ margin: '0 0 8px', fontSize: '0.72rem' }}>
                  Line icon pack. Works offline.
                </p>
                <ElementGrid items={icons} onInsert={onInsertElement} />
              </>
            )}

            {elementSection === 'charts' && (
              <>
                <p className="field-hint" style={{ margin: '0 0 8px', fontSize: '0.72rem' }}>
                  Insert a chart graphic (edit numbers later in a future release).
                </p>
                <ElementGrid items={charts} onInsert={onInsertElement} />
              </>
            )}

            {elementSection === 'brand' && (
              <div className="brand-pane">
                <p className="field-hint" style={{ margin: '0 0 10px', fontSize: '0.72rem' }}>
                  {DEFAULT_BRAND_KIT.name} — click a color to apply to selection
                </p>
                <h4 className="brand-section-title">Colors</h4>
                <div className="brand-colors">
                  {DEFAULT_BRAND_KIT.colors.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="brand-swatch"
                      title={`${c.name} ${c.hex}`}
                      style={{ background: c.hex }}
                      onClick={() => onApplyBrandColor?.(c.hex)}
                    />
                  ))}
                </div>
                <h4 className="brand-section-title">Fonts</h4>
                <div className="brand-fonts">
                  {DEFAULT_BRAND_KIT.fonts.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      className="brand-font-btn"
                      style={{
                        fontFamily: f.family,
                        fontWeight: f.fontWeight as number,
                      }}
                      onClick={() => onInsertElement(brandFontAsElement(f))}
                    >
                      {f.sample}
                      <span>{f.name}</span>
                    </button>
                  ))}
                </div>
                <h4 className="brand-section-title">Logo</h4>
                <ElementGrid
                  items={[DEFAULT_BRAND_KIT.logo]}
                  onInsert={onInsertElement}
                />
              </div>
            )}

            {!['photos', 'icons', 'charts', 'brand'].includes(elementSection) && (
              <ElementGrid items={elements} onInsert={onInsertElement} />
            )}
          </>
        )}

        {tab === 'photo-studio' && (
          <PhotoStudioPanel
            projectId={projectId}
            onPlaceImage={(url) => onInsertUploadUrl?.(url)}
            onUploadFile={onUploadImage}
          />
        )}

        {tab === 'uploads' && (
          <div className="uploads-pane">
            <p className="field-hint">
              Choose an image, take a photo, or drag onto the artboard. Signed-in
              uploads sync to Storage. Select a Frame first to clip into it.
            </p>
            <div className="upload-actions">
              <label
                className={`btn btn-secondary btn-block upload-label ${busy ? 'disabled' : ''}`}
              >
                {busy ? 'Uploading…' : 'Choose image'}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  disabled={busy || !onUploadImage}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file || !onUploadImage) return;
                    setLocalBusy(true);
                    void Promise.resolve(onUploadImage(file)).finally(() =>
                      setLocalBusy(false)
                    );
                  }}
                />
              </label>
              <label
                className={`btn btn-secondary btn-block upload-label ${busy ? 'disabled' : ''}`}
              >
                Take photo
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  disabled={busy || !onUploadImage}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file || !onUploadImage) return;
                    setLocalBusy(true);
                    void Promise.resolve(onUploadImage(file)).finally(() =>
                      setLocalBusy(false)
                    );
                  }}
                />
              </label>
            </div>
            {onUploadVideo && (
              <>
                <h4 className="brand-section-title">Video</h4>
                <p className="field-hint">
                  MP4, WebM or MOV up to 100 MB. Clips play on the motion timeline and export
                  frame-accurately.
                </p>
                <div className="upload-actions">
                  <label
                    className={`btn btn-secondary btn-block upload-label ${busy ? 'disabled' : ''}`}
                  >
                    {busy ? 'Importing…' : 'Choose video'}
                    <input
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime,.mp4,.m4v,.webm,.mov"
                      hidden
                      disabled={busy}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (!file) return;
                        setLocalBusy(true);
                        void Promise.resolve(onUploadVideo(file)).finally(() =>
                          setLocalBusy(false)
                        );
                      }}
                    />
                  </label>
                </div>
              </>
            )}
            {uploadNote && <p className="field-hint upload-note">{uploadNote}</p>}
            {recentUploads.length > 0 && (
              <>
                <h4 className="brand-section-title">This session</h4>
                <div className="upload-grid">
                  {recentUploads.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="upload-thumb"
                      title={`${item.name} (${item.source === 'storage' ? 'cloud' : 'local'})`}
                      onClick={() => onInsertUploadUrl?.(item.url)}
                    >
                      <img src={item.url} alt={item.name} />
                      <span>{item.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
